"use client";

/**
 * The website's navigation.
 *
 * This replaces the landing page's old header, which was a logo and four anchor links
 * pointing at four sections of the same page. That is a header for a one page site: it
 * tells a visitor there is nothing else here, and it gave the four capabilities the site
 * is actually built on no room to say anything.
 *
 * Website only, deliberately. The phone app never renders this: middleware rewrites "/"
 * to /welcome for native requests and sends every other marketing route the same way
 * (see lib/native-shell.ts and lib/middleware/auth-proxy.ts), so a member holding the app
 * is never shown a "download the app" nav.
 *
 * The two panels are the whole point of the header. Each row states a capability AND the
 * outcome it produces, because "Events" on its own persuades nobody: "fill a room without
 * an audience to invite" does. That means the nav teaches something even to a visitor who
 * never clicks it.
 */

import { useCallback, useEffect, useId, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  Building2,
  CalendarDays,
  ChevronDown,
  Compass,
  Handshake,
  Menu,
  Rocket,
  Sparkles,
  Trophy,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import { buttonClasses } from "@/lib/ui/buttonClasses";
import { LogoServer } from "@/components/ui/logo-server";
import { CompactLanguageSelector } from "@/components/ui/compact-language-selector";
import { useTranslations } from "@/hooks/use-translations";
import { cn, isAndroid } from "@/lib/utils";

type NavItem = {
  href: string;
  icon: LucideIcon;
  nameKey: string;
  taglineKey: string;
};

const PRODUCT_ITEMS: NavItem[] = [
  { href: "/features/discover", icon: Compass, nameKey: "features.discover.name", taglineKey: "features.discover.tagline" },
  { href: "/features/events", icon: CalendarDays, nameKey: "features.events.name", taglineKey: "features.events.tagline" },
  { href: "/features/communities", icon: Users, nameKey: "features.communities.name", taglineKey: "features.communities.tagline" },
  { href: "/features/opportunities", icon: Handshake, nameKey: "features.opportunities.name", taglineKey: "features.opportunities.tagline" },
  { href: "/features/reputation", icon: Trophy, nameKey: "features.reputation.name", taglineKey: "features.reputation.tagline" },
];

const AUDIENCE_ITEMS: NavItem[] = [
  { href: "/for/founders", icon: Rocket, nameKey: "audiences.founders.name", taglineKey: "audiences.founders.tagline" },
  { href: "/for/business-owners", icon: Building2, nameKey: "audiences.owners.name", taglineKey: "audiences.owners.tagline" },
];

const COMPANY_LINKS = [
  { href: "/about", nameKey: "company.about" },
  { href: "/how-it-works", nameKey: "company.howItWorks" },
  { href: "/faq", nameKey: "company.faq" },
  { href: "/safety", nameKey: "company.safety" },
  { href: "/contact", nameKey: "company.contact" },
];

function PanelRow({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  const { t } = useTranslations("site");
  const Icon = item.icon;

  return (
    <Link
      href={item.href}
      onClick={onNavigate}
      className="group flex items-start gap-3 rounded-2xl p-3 transition-colors hover:bg-primary-lighter focus-visible:bg-primary-lighter focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
    >
      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-white">
        <Icon className="h-[18px] w-[18px]" aria-hidden />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-bold leading-snug text-neutral-900">{t(item.nameKey)}</span>
        <span className="mt-0.5 block text-[13px] leading-snug text-neutral-500">{t(item.taglineKey)}</span>
      </span>
    </Link>
  );
}

/**
 * One desktop dropdown. Opens on hover AND on click, closes on Escape, on outside click and
 * when focus leaves the whole group, so it is usable with a keyboard rather than only a mouse.
 */
function DesktopMenu({
  label,
  blurb,
  items,
  footerHref,
  footerLabel,
  isOpen,
  onOpen,
  onClose,
}: {
  label: string;
  blurb: string;
  items: NavItem[];
  footerHref: string;
  footerLabel: string;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}) {
  const panelId = useId();

  return (
    <div
      className="relative"
      onMouseEnter={onOpen}
      onMouseLeave={onClose}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) onClose();
      }}
    >
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => (isOpen ? onClose() : onOpen())}
        className={cn(
          "inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-semibold transition-colors",
          isOpen ? "bg-neutral-100 text-neutral-900" : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900",
        )}
      >
        {label}
        <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", isOpen && "rotate-180")} aria-hidden />
      </button>

      {/* Rendered always, hidden with opacity/translate, so the panel animates rather than
          appearing instantly, and so its links are removed from the tab order when closed. */}
      <div
        id={panelId}
        className={cn(
          /* max-w-[34rem] is load-bearing, not belt and braces. globals.css carries an
             unlayered `*:not([class*="max-w"])… { max-width: 100% }` rule to stop containers
             sprawling, and its :not() chain outweighs any single utility class. Without a
             max-w-* class this panel inherits max-width: 100% of its containing block, which
             is the 98px nav button, and the 34rem width collapses to a one character column.
             Naming a max-w class is the opt out the rule is designed around.

             Left aligned to its trigger rather than centred on it: a 544px panel centred on the
             first nav item hangs 30px off the left edge of a 1024px viewport, and the page
             cannot scroll to reveal it because html carries overflow-x: hidden. */
          "absolute left-0 top-full z-50 w-[min(34rem,calc(100vw-3rem))] max-w-[34rem] pt-3 transition-all duration-200",
          isOpen ? "visible translate-y-0 opacity-100" : "invisible -translate-y-1 opacity-0",
        )}
        {...(isOpen ? {} : { inert: "" as unknown as boolean })}
      >
        <div className="overflow-hidden rounded-3xl border border-neutral-200/80 bg-white shadow-[0_24px_60px_-20px_rgba(15,23,42,0.28)]">
          <p className="border-b border-neutral-100 px-5 py-3 text-[13px] leading-snug text-neutral-500">{blurb}</p>
          <div className="grid gap-1 p-2 sm:grid-cols-2">
            {items.map((item) => (
              <PanelRow key={item.href} item={item} onNavigate={onClose} />
            ))}
          </div>
          <Link
            href={footerHref}
            onClick={onClose}
            className="flex items-center gap-2 border-t border-neutral-100 bg-neutral-50/80 px-5 py-3 text-[13px] font-semibold text-primary transition-colors hover:bg-primary-lighter"
          >
            <Sparkles className="h-4 w-4" aria-hidden />
            {footerLabel}
          </Link>
        </div>
      </div>
    </div>
  );
}

export function SiteNav() {
  const { t } = useTranslations("site");
  const { t: tLanding } = useTranslations("landing");
  const pathname = usePathname();
  const [openMenu, setOpenMenu] = useState<"product" | "audience" | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLElement | null>(null);

  /* Android draws the WebView under the status bar, so the header needs its own padding
     there. Computed once at first paint rather than in an effect, to avoid the logo
     jumping down a row after hydration. */
  const androidPlatform = typeof window !== "undefined" ? isAndroid() : false;

  const closeAll = useCallback(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, []);

  /* Route change closes everything, including a browser back that no link handler saw.
     Adjusted during render rather than in an effect: React documents this as the way to
     reset state when a prop changes, and it avoids the extra render (and the flash of an
     open panel over the new page) that an effect would cause. */
  const [renderedPath, setRenderedPath] = useState(pathname);
  if (pathname !== renderedPath) {
    setRenderedPath(pathname);
    setOpenMenu(null);
    setMobileOpen(false);
  }

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeAll();
    };
    const onPointerDown = (event: PointerEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) setOpenMenu(null);
    };
    const onScroll = () => setScrolled(window.scrollY > 8);

    /* Read the scroll position on the next frame rather than during the effect. Setting
       state synchronously here triggers a second render before paint, which the repo's
       lint rule (and React) both flag. */
    const frame = requestAnimationFrame(onScroll);
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("scroll", onScroll);
    };
  }, [closeAll]);

  // The mobile sheet is the scrolling surface while it is open, so the page behind it
  // must not scroll with it. Restored on close, including if the component unmounts first.
  useEffect(() => {
    if (!mobileOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileOpen]);

  return (
    <header
      ref={headerRef}
      className={cn(
        "sticky top-0 z-50 bg-[#fafafa]/90 backdrop-blur-md transition-shadow duration-200",
        scrolled ? "border-b border-neutral-200/80 shadow-[0_1px_0_rgba(15,23,42,0.04)]" : "border-b border-transparent",
      )}
      style={{ paddingTop: androidPlatform ? "calc(env(safe-area-inset-top, 24px) + 6px)" : "var(--sat)" }}
    >
      {/* The row had no vertical padding at all, so the bar was exactly as tall as the logo and the
          mark sat hard against the top edge of the page (Tom, 2026-09-21: "logo too close to the
          part above it, the top nav needs to be thicker"). */}
      <div className="gs-page flex min-w-0 items-center justify-between gap-2 py-3 md:py-4">
        <LogoServer href="/" variant="compact" className={androidPlatform ? "h-8 w-8 md:h-11 md:w-11" : "h-9 w-9 md:h-11 md:w-11"} />

        {/* gap-0.5 was 2px, so on a wide screen the four items read as one bunched block with
            acres of space either side of it. Opens up from 1280 where there is room, and stays
            tight at 1024 where the four items plus the sign-in cluster only just fit. */}
        <nav
          className="hidden items-center gap-0.5 lg:flex xl:gap-2 2xl:gap-3"
          aria-label={t("nav.menu")}
        >
          <DesktopMenu
            label={t("nav.product")}
            blurb={t("nav.productBlurb")}
            items={PRODUCT_ITEMS}
            footerHref="/features"
            footerLabel={t("nav.allFeatures")}
            isOpen={openMenu === "product"}
            onOpen={() => setOpenMenu("product")}
            onClose={() => setOpenMenu(null)}
          />
          <DesktopMenu
            label={t("nav.whoItsFor")}
            blurb={t("nav.audienceBlurb")}
            items={AUDIENCE_ITEMS}
            footerHref="/how-it-works"
            footerLabel={t("company.howItWorks")}
            isOpen={openMenu === "audience"}
            onOpen={() => setOpenMenu("audience")}
            onClose={() => setOpenMenu(null)}
          />
          <Link
            href="/pricing"
            className="rounded-lg px-3 py-2 text-sm font-semibold !text-neutral-600 transition-colors hover:bg-neutral-100 hover:!text-neutral-900"
          >
            {t("nav.pricing")}
          </Link>
          <Link
            href="/about"
            className="rounded-lg px-3 py-2 text-sm font-semibold !text-neutral-600 transition-colors hover:bg-neutral-100 hover:!text-neutral-900"
          >
            {t("nav.company")}
          </Link>
        </nav>

        <div className="flex shrink-0 items-center gap-2 md:gap-3">
          <div className="hidden sm:block">
            <CompactLanguageSelector />
          </div>
          <Link
            href="/auth/login"
            className={buttonClasses({ variant: "ghost", size: "sm", className: "!whitespace-nowrap hidden sm:inline-flex" })}
          >
            {tLanding("signIn")}
          </Link>
          <Link href="/auth/signup" className={buttonClasses({ size: "sm", className: "!min-w-0 !whitespace-nowrap" })}>
            <span className="!truncate !whitespace-nowrap sm:hidden">{tLanding("signUp")}</span>
            <span className="hidden !truncate !whitespace-nowrap sm:inline">{tLanding("getStartedForFree")}</span>
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label={t("nav.openMenu")}
            aria-expanded={mobileOpen}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-neutral-700 transition-colors hover:bg-neutral-100 lg:hidden"
          >
            <Menu className="h-5 w-5" aria-hidden />
          </button>
        </div>
      </div>

      {/* Mobile sheet. Full screen rather than a dropdown: there are fourteen destinations
          now, and a cramped dropdown list would hide most of them below the fold. */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden" role="dialog" aria-modal="true" aria-label={t("nav.menu")}>
          <button
            type="button"
            aria-label={t("nav.closeMenu")}
            onClick={() => setMobileOpen(false)}
            className="absolute inset-0 h-full w-full cursor-default bg-neutral-900/30 backdrop-blur-[2px]"
          />
          <div
            className="absolute inset-x-0 top-0 max-h-[100dvh] overflow-y-auto overscroll-contain rounded-b-[2rem] bg-white pb-8 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.4)]"
            style={{ paddingTop: "var(--sat)" }}
          >
            <div className="flex items-center justify-between px-5 py-3">
              <LogoServer href="/" variant="compact" className="h-9 w-9" />
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                aria-label={t("nav.closeMenu")}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full text-neutral-600 transition-colors hover:bg-neutral-100"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>

            <div className="px-3 pb-2">
              <p className="px-3 pb-1 pt-3 gs-eyebrow text-neutral-500">{t("nav.product")}</p>
              {PRODUCT_ITEMS.map((item) => (
                <PanelRow key={item.href} item={item} onNavigate={() => setMobileOpen(false)} />
              ))}
              <Link
                href="/features"
                onClick={() => setMobileOpen(false)}
                className="mx-3 mt-1 flex items-center gap-2 rounded-2xl bg-primary-lighter px-3 py-3 text-[13px] font-semibold text-primary"
              >
                <Sparkles className="h-4 w-4" aria-hidden />
                {t("nav.allFeatures")}
              </Link>

              <p className="px-3 pb-1 pt-6 gs-eyebrow text-neutral-500">{t("nav.whoItsFor")}</p>
              {AUDIENCE_ITEMS.map((item) => (
                <PanelRow key={item.href} item={item} onNavigate={() => setMobileOpen(false)} />
              ))}

              <p className="px-3 pb-1 pt-6 gs-eyebrow text-neutral-500">{t("nav.company")}</p>
              <div className="grid grid-cols-2 gap-1 px-1">
                <Link
                  href="/pricing"
                  onClick={() => setMobileOpen(false)}
                  className="rounded-2xl px-3 py-3 text-sm font-semibold text-neutral-800 transition-colors hover:bg-neutral-100"
                >
                  {t("nav.pricing")}
                </Link>
                {COMPANY_LINKS.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileOpen(false)}
                    className="rounded-2xl px-3 py-3 text-sm font-semibold text-neutral-800 transition-colors hover:bg-neutral-100"
                  >
                    {t(link.nameKey)}
                  </Link>
                ))}
              </div>

              <div className="mt-6 flex items-center gap-3 px-3">
                <Link
                  href="/auth/signup"
                  onClick={() => setMobileOpen(false)}
                  className={buttonClasses({ size: "md", fullWidth: true })}
                >
                  {tLanding("getStartedForFree")}
                </Link>
              </div>
              <div className="mt-3 flex items-center justify-between gap-3 px-3">
                <Link
                  href="/auth/login"
                  onClick={() => setMobileOpen(false)}
                  className="inline-flex min-h-11 items-center text-sm font-semibold text-neutral-600"
                >
                  {tLanding("signIn")}
                </Link>
                <CompactLanguageSelector />
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
